import React, { useEffect, useState } from "react";
import "../styles/LandingPage.css";

import {
  getHomepage,
  mediaUrl,
  normalizeHero,
  isHeroSection,
  getSchulprofil,
  getEinblicke,
  getTermine,
  getTeachers,
  STRAPI_URL,
} from "../lib/api";

import HeroStack from "../components/HeroStack";
import IconByName from "../components/IconByName";

export default function LandingPage() {
  const [home, setHome] = useState(null);
  const [error, setError] = useState("");
  const [scrollY, setScrollY] = useState(0);
  const [profil, setProfil] = useState(null);
  const [einblicke, setEinblicke] = useState([]);
  const [termine, setTermine] = useState([]);
  const [teamPhoto, setTeamPhoto] = useState(null);
  // Cards, Einblicke and Termine all use the same horizontal-scroll
  // carousel pattern (track ref + overflow/atStart/atEnd state + a
  // scroll-by-one-item handler). One hook, three call sites, instead
  // of three copies of the same effect and handler.
  //
  // Cards and Einblicke render their track as a plain CSS grid on
  // desktop/tablet (see the "ls-carousel-track--grid" class below) —
  // a grid never overflows horizontally, so this hook naturally
  // reports overflow=false there and the arrows stay hidden on their
  // own, with no extra logic needed. Termine keeps the track as an
  // always-on scrolling row, so its arrows can show at any width.
  const cardsCarousel = useCarousel(".ls-card", [profil]);
  const postsCarousel = useCarousel(".ls-post", [einblicke]);
  const termineCarousel = useCarousel(".ls-event", [termine]);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY || 0);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    let cancelled = false;
    getHomepage()
      .then((data) => { if (!cancelled) setHome(data); })
      .catch((e) => { if (!cancelled) setError(String(e.message || e)); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    getSchulprofil().then(setProfil).catch(console.error);
    getEinblicke().then((posts) => setEinblicke(posts.slice(0, 3))).catch(console.error);
    getTermine().then((events) => {
      const today = new Date().toISOString().slice(0, 10);
      setTermine(events.filter((e) => e.date >= today).slice(0, 5));
    }).catch(console.error);
  }, []);

  // Separate effect — runs once home is loaded
  useEffect(() => {
    if (!home) return;
    const photo = home.sections?.[0]?.teamPhoto ?? null;
    setTeamPhoto(photo);
  }, [home]);

  if (error) {
    return (
      <main className="landing">
        <ParallaxBackground scrollY={scrollY} />
        <section className="landing-hero hero-split">
          <div className="hero-inner container">
            <div className="hero-copy">
              <p className="hero-title font-handwritten">ROTHENBURGSCHULE</p>
              <h1>Fehler beim Laden</h1>
              <p className="ls-muted">{error}</p>
            </div>
          </div>
        </section>
      </main>
    );
  }

  if (!home) {
    return (
      <main className="landing">
        <ParallaxBackground scrollY={scrollY} />
        <section className="landing-hero hero-split">
          <div className="hero-inner container">
            <div className="hero-copy">
              <div className="skeleton ls-skeleton-h1" />
              <div className="skeleton line" />
              <div className="hero-ctas">
                <span className="skeleton ls-action" />
                <span className="skeleton ls-action" />
              </div>
            </div>
            <div className="hero-art"><div className="skeleton image" /></div>
          </div>
        </section>
      </main>
    );
  }

  const sections = Array.isArray(home.sections) ? home.sections : [];
  const heroRaw = sections.find(isHeroSection) || {};
  const hero = normalizeHero(heroRaw);
  const heroImgSrc = mediaUrl(hero.heroImage);
  const eyebrow = buildEyebrow(heroRaw);

  const MONTHS_SHORT = ["Jän", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];

  return (
    <main className="landing">
      <ParallaxBackground scrollY={scrollY} />

      {/* ── Hero ────────────────────────────────────── */}
      <section className="landing-hero hero-split">
        <div className="hero-bg">
          <div className="blob blob-a" />
          <div className="blob blob-b" />
          <div className="dots" aria-hidden="true" />
        </div>

        <div className="hero-inner container">
          <div className="hero-copy">
            {/* Eyebrow: „Zentrum für Begabungsförderung" + handgezeichneter Strich.
                Varianten zum Ausprobieren: hero-eyebrow--mixed | --split | --oneline */}
            {eyebrow.lead || eyebrow.main ? (
              <div className="hero-eyebrow">
                <p className="hero-eyebrow__text">
                  {eyebrow.lead ? <span className="hero-eyebrow__lead">{eyebrow.lead}</span> : null}
                  {eyebrow.main ? <span className="hero-eyebrow__main">{eyebrow.main}</span> : null}
                </p>
                <svg className="hero-eyebrow__stroke" viewBox="0 0 420 18" aria-hidden="true">
                  <path d="M4 12.4C86 6.2 196 4.4 292 5.6c56 .7 98 2.8 124 5.8-28-.8-68-2.3-124-2.8-96-.8-206 .8-288 3.8Z" />
                </svg>
              </div>
            ) : null}

            <p className="hero-title font-handwritten">{hero.heroTitle}</p>

            <TypingChipLoop
              preamble={hero.heroPreamble || ""}
              words={Array.isArray(hero.heroTypingWords) ? hero.heroTypingWords : []}
            />

            <div className="hero-ctas">
              {hero.ctaPrimaryLabel && (
                <a className="ls-action ls-action--primary" href={hero.ctaPrimaryUrl}>{hero.ctaPrimaryLabel}</a>
              )}
              {hero.ctaSecondaryLabel && (
                <a className="ls-action ls-action--ghost" href={hero.ctaSecondaryUrl}>{hero.ctaSecondaryLabel}</a>
              )}
            </div>
          </div>

          <div className="hero-art">
            <HeroStack images={buildHeroImages(hero, heroImgSrc, mediaUrl)} />
          </div>
        </div>

        <div className="wave-bottom" aria-hidden="true" />
      </section>

      {/* ── Features / Schulprofil cards ───────────── */}
      {profil?.editorialBlocks?.length > 0 ? (
        <section className="ls-section">
          <div className="container">
            <div className="ls-section-head">
              <div>
                <p className="ls-kicker">{home.featuresKicker}</p>
                <h2 className="ls-h2">{home.featuresTitle}</h2>
                <div className="ls-section-accent" aria-hidden="true" />
              </div>
              <a className="ls-action ls-action--ghost" href={home.featuresButtonUrl}>{home.featuresButtonLabel}</a>
            </div>
            <div className="ls-carousel-wrap">
              {cardsCarousel.overflow && !cardsCarousel.atStart ? (
                <CarouselArrow direction="left" onClick={() => cardsCarousel.scroll("left")} label="Vorherige anzeigen" />
              ) : null}
              {/* "--grid" = original full-width grid on desktop/tablet;
                  only becomes the scrolling carousel at ≤640px (see CSS). */}
              <div className="ls-carousel-track ls-carousel-track--grid" ref={cardsCarousel.trackRef}>
                {profil.editorialBlocks.slice(0, 3).map((block, i) => (
                  <article className="ls-card" key={i}>
                    <div className="ls-card__icon">
                      <IconByName name={block.icon || "star"} />
                    </div>
                    <h3>{block.title}</h3>
                    <p>{block.text}</p>
                  </article>
                ))}
              </div>
              {cardsCarousel.overflow && !cardsCarousel.atEnd ? (
                <CarouselArrow direction="right" onClick={() => cardsCarousel.scroll("right")} label="Weitere anzeigen" />
              ) : null}
            </div>
          </div>
        </section>
      ) : null}

      {/* ── Einblicke teaser ───────────────────────── */}
      {einblicke.length > 0 ? (
        <section className="ls-section">
          <div className="container">
            <div className="ls-section-head">
              <div>
                <p className="ls-kicker">{home.einblickeKicker}</p>
                <h2 className="ls-h2">{home.einblickeTitle}</h2>
                <div className="ls-section-accent" aria-hidden="true" />
              </div>
              <a className="ls-action ls-action--ghost" href={home.einblickeButtonUrl}>{home.einblickeButtonLabel}</a>
            </div>
            <div className="ls-carousel-wrap">
              {postsCarousel.overflow && !postsCarousel.atStart ? (
                <CarouselArrow direction="left" onClick={() => postsCarousel.scroll("left")} label="Vorherige anzeigen" />
              ) : null}
              {/* "--grid" = original full-width grid on desktop/tablet;
                  only becomes the scrolling carousel at ≤640px (see CSS). */}
              <div className="ls-carousel-track ls-carousel-track--grid" ref={postsCarousel.trackRef}>
                {einblicke.map((post) => (
                  <a
                    className="ls-post"
                    key={post.id}
                    href={`/einblicke?post=${post.documentId ?? post.id}`}
                  >
                    {post.image ? (
                      <figure className="ls-post__image">
                        <img src={post.image} alt={post.title} loading="lazy" />
                      </figure>
                    ) : (
                      <div className="ls-post__image ls-post__image--fallback">
                        <div className="ls-media-fallback">RS</div>
                      </div>
                    )}
                    <div className="ls-post__body">
                      <div className="ls-post__meta">
                        {post.category ? <span className="ls-pill">{post.category}</span> : null}
                        {post.date ? <span className="ls-date">{post.date}</span> : null}
                      </div>
                      <h3>{post.title}</h3>
                      <p>{post.excerpt}</p>
                    </div>
                  </a>
                ))}
              </div>
              {postsCarousel.overflow && !postsCarousel.atEnd ? (
                <CarouselArrow direction="right" onClick={() => postsCarousel.scroll("right")} label="Weitere anzeigen" />
              ) : null}
            </div>
          </div>
        </section>
      ) : null}

      {/* ── Termine teaser ─────────────────────────── */}
      {termine.length > 0 ? (
        <section className="ls-section">
          <div className="container">
            <div className="ls-section-head">
              <div>
                <p className="ls-kicker">{home.termineKicker}</p>
                <h2 className="ls-h2">{home.termineTitle}</h2>
                <div className="ls-section-accent" aria-hidden="true" />
              </div>
              <a className="ls-action ls-action--ghost" href={home.termineButtonUrl}>{home.termineButtonLabel}</a>
            </div>
            <div className="ls-carousel-wrap">
              {termineCarousel.overflow && !termineCarousel.atStart ? (
                <CarouselArrow direction="left" onClick={() => termineCarousel.scroll("left")} label="Frühere Termine anzeigen" />
              ) : null}
              {/* Termine stays an always-on scrolling agenda row at every
                  width — unlike Cards/Einblicke, no "--grid" class here. */}
              <div className="ls-carousel-track" ref={termineCarousel.trackRef}>
                {termine.map((event) => {
                  const d = new Date(event.date);
                  return (
                    <article className="ls-event" key={event.id}>
                      <div className="ls-event__date">
                        <span className="ls-event__day">{d.getDate()}</span>
                        <div className="ls-event__month-year">
                          <span className="ls-event__month">{MONTHS_SHORT[d.getMonth()]}</span>
                          <span className="ls-event__year">{d.getFullYear()}</span>
                        </div>
                      </div>
                      <div className="ls-event__body">
                        <h3>{event.title}</h3>
                        {event.time ? <p className="ls-event__time">{event.time}</p> : null}
                        {event.location && event.location !== "—" ? (
                          <p className="ls-event__location">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 21s6-5 6-10a6 6 0 10-12 0c0 5 6 10 6 10z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round"/><circle cx="12" cy="11" r="2" stroke="currentColor" strokeWidth="1.7"/></svg>
                            {event.location}
                          </p>
                        ) : null}
                      </div>
                    </article>
                  );
                })}
              </div>
              {termineCarousel.overflow && !termineCarousel.atEnd ? (
                <CarouselArrow direction="right" onClick={() => termineCarousel.scroll("right")} label="Weitere Termine anzeigen" />
              ) : null}
            </div>
          </div>
        </section>
      ) : null}

      {/* ── Team teaser ────────────────────────────── */}
      <section className="ls-teaser-section team-teaser">
        <div className="container">
          <div className="ls-teaser-head ls-teaser-head--cta">
            <div>
              <p className="ls-kicker">{home.teamKicker}</p>
              <h2 className="ls-h2">{home.teamTitle}</h2>
              <div className="ls-section-accent" aria-hidden="true" />
              <p className="ls-muted team-teaser__intro">
                {home.teamText}
              </p>
            </div>
            <a className="ls-action ls-action--ghost" href={home.teamButtonUrl}>{home.teamButtonLabel}</a>
          </div>
          {teamPhoto ? (
            <>
              <svg width="0" height="0" style={{ position: "absolute" }}>
                <defs>
                  <clipPath id="wavy-clip" clipPathUnits="objectBoundingBox">
                    <path d="
                      M 0.02,0.05
                      C 0.08,0.00 0.18,0.03 0.25,0.01
                      C 0.33,-0.01 0.40,0.04 0.50,0.02
                      C 0.60,0.00 0.67,0.04 0.75,0.02
                      C 0.83,-0.01 0.92,0.03 0.98,0.05
                      C 1.02,0.08 1.00,0.18 0.99,0.25
                      C 0.97,0.33 1.01,0.42 0.99,0.50
                      C 0.97,0.58 1.01,0.67 0.99,0.75
                      C 0.97,0.83 1.02,0.92 0.98,0.95
                      C 0.92,1.01 0.83,0.97 0.75,0.99
                      C 0.67,1.01 0.60,0.97 0.50,0.99
                      C 0.40,1.01 0.33,0.97 0.25,0.99
                      C 0.18,1.01 0.08,0.97 0.02,0.95
                      C -0.02,0.92 0.01,0.83 0.01,0.75
                      C 0.01,0.67 -0.02,0.58 0.01,0.50
                      C 0.03,0.42 -0.01,0.33 0.01,0.25
                      C 0.03,0.17 -0.02,0.08 0.02,0.05
                      Z
                    " />
                  </clipPath>
                </defs>
              </svg>

              <figure className="team-teaser__photo">
                <img src={teamPhoto.url} alt={teamPhoto.alt} loading="lazy" />
              </figure>
            </>
          ) : null}
        </div>
      </section>
    </main>
  );
}

/* ── Helpers ──────────────────────────────────── */

/**
 * Shared by the feature-card, Einblicke and Termine strips: a
 * horizontally-scrolling track plus the overflow/atStart/atEnd state
 * that decides which arrow buttons to show. `itemSelector` is the
 * class of one row item, used to measure how far to scroll per click.
 */
function useCarousel(itemSelector, deps) {
  const trackRef = React.useRef(null);
  const [overflow, setOverflow] = useState(false);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;
    const update = () => {
      const maxScroll = track.scrollWidth - track.clientWidth;
      setOverflow(maxScroll > 4);
      setAtStart(track.scrollLeft <= 4);
      setAtEnd(track.scrollLeft >= maxScroll - 4);
    };
    update();
    track.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      track.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  const scroll = (direction) => {
    const track = trackRef.current;
    if (!track) return;
    const item = track.querySelector(itemSelector);
    const step = item ? item.getBoundingClientRect().width + 16 : track.clientWidth * 0.8;
    track.scrollBy({ left: direction === "left" ? -step : step, behavior: "smooth" });
  };

  return { trackRef, overflow, atStart, atEnd, scroll };
}

function CarouselArrow({ direction, onClick, label }) {
  const isLeft = direction === "left";
  return (
    <button
      type="button"
      className={`ls-carousel-arrow ls-carousel-arrow--${direction}`}
      onClick={onClick}
      aria-label={label}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d={isLeft ? "M15 6l-6 6 6 6" : "M9 6l6 6-6 6"}
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}

function ParallaxBackground({ scrollY }) {
  return (
    <>
      {/* langsame Blobs — fixed */}
      <div className="landing-parallax-bg" aria-hidden="true"
           style={{ transform: `translateY(${scrollY * 0.09}px)` }}>
        <img src="/background/blobnew1.svg" alt="" className="landing-parallax-svg svg-1" />
        <img src="/background/blobnew2.svg" alt="" className="landing-parallax-svg svg-2" />
        <img src="/background/blobnew3.svg" alt="" className="landing-parallax-svg svg-3" />
        <img src="/background/blobnew4.svg" alt="" className="landing-parallax-svg svg-4" />
      </div>
    </>
  );
}

/**
 * Eyebrow-Text über dem Titel.
 * 1. Wenn in Strapi heroEyebrowLead / heroEyebrowMain gepflegt sind → diese nehmen.
 * 2. Sonst heroNoteText am letzten Leerzeichen teilen
 *    ("Zentrum für" / "Begabungsförderung").
 * 3. Sonst Fallback-Text.
 */
function buildEyebrow(heroRaw = {}) {
  const lead = String(heroRaw.heroEyebrowLead ?? "").trim();
  const main = String(heroRaw.heroEyebrowMain ?? "").trim();
  if (lead || main) return { lead, main };

  const full = String(heroRaw.heroNoteText ?? "Zentrum für Begabungsförderung").trim();
  if (!full) return { lead: "", main: "" };

  const i = full.lastIndexOf(" ");
  if (i < 0) return { lead: "", main: full };
  return { lead: full.slice(0, i), main: full.slice(i + 1) };
}

function buildHeroImages(hero, fallbackSrc, mediaUrlFn) {
  const urls = [];
  const pushUrl = (u) => {
    if (!u) return;
    const abs = u.startsWith("http") ? u : mediaUrlFn ? mediaUrlFn({ url: u }) : u;
    if (abs && !urls.includes(abs)) urls.push(abs);
  };
  const collect = (v) => {
    if (!v) return;
    if (typeof v === "string") { pushUrl(v); return; }
    if (Array.isArray(v)) { v.forEach(collect); return; }
    if (v.data) { collect(v.data); return; }
    if (v.url) pushUrl(v.url);
    if (v.attributes?.url) pushUrl(v.attributes.url);
    if (v.image) collect(v.image);
  };
  collect(hero?.heroImages);
  if (!urls.length) collect(hero?.heroImage);
  if (!urls.length && fallbackSrc) pushUrl(fallbackSrc);
  return urls.slice(0, 3).map((src, idx) => ({ src, alt: "", ariaLabel: `Bild ${idx + 1}` }));
}

function TypingChipLoop({ preamble = "", words = [], typingSpeed = 130, deletingSpeed = 80, pause = 1500, between = 300 }) {
  const safe = Array.isArray(words) ? words.filter(Boolean) : [];
  const [i, setI] = React.useState(0);
  const [text, setText] = React.useState("");
  const [phase, setPhase] = React.useState("typing");

  React.useEffect(() => { setI(0); setText(""); setPhase("typing"); }, [safe.join("|")]);

  React.useEffect(() => {
    if (!safe.length) return undefined;
    const full = safe[i] || "";
    let timeoutId;
    if (phase === "typing") {
      if (text.length < full.length) timeoutId = setTimeout(() => setText(full.slice(0, text.length + 1)), typingSpeed);
      else setPhase("pausing");
    }
    if (phase === "pausing") timeoutId = setTimeout(() => setPhase("deleting"), pause);
    if (phase === "deleting") {
      if (text.length > 0) timeoutId = setTimeout(() => setText(full.slice(0, text.length - 1)), deletingSpeed);
      else setPhase("between");
    }
    if (phase === "between") timeoutId = setTimeout(() => { setI((prev) => (prev + 1) % safe.length); setPhase("typing"); }, between);
    return () => clearTimeout(timeoutId);
  }, [safe, i, text, phase, typingSpeed, deletingSpeed, pause, between]);

  if (!safe.length) return preamble ? <p className="typing-line">{preamble}</p> : null;

  return (
    <div className="hero-typing">
      <p className="typing-line" aria-live="polite">
        {preamble ? <span className="typing-preamble">{preamble}</span> : null}
        <span className="typing-wrap typing-wrap--line2">
          <span className={`typing-word ${phase === "pausing" ? "is-done" : ""}`}>{text || "\u00A0"}</span>
          <span className="typing-caret" aria-hidden="true" />
        </span>
      </p>
    </div>
  );
}